import { updateId } from 'utils/generate-id';
import { createIdsMap, updateReferenceIds } from 'common/utils/blocks/update-reference-ids';
import AdminError from 'admin/components/common/ErrorBoundary/AdminError';
import { SymbolManager, type GenerateIdCallback } from 'common/utils/blocks/symbol-manager';
import { set } from 'lodash';

import {
	BBModel,
	BBModelTextChild,
	BBSymbolInstance,
	CardData,
	CardFlowEvent,
	StoryModel,
	StorySettingsType,
	StoryVersionType,
	type StoryMediaPlatform,
	BBStates,
	type BBSymbolLink,
} from 'types/story';
import { CARD_TYPE, COMPONENT_STATES, COMPONENT_TYPE, CountType } from 'common/constants';
import { Counter, deepClone } from 'utils/helpers';
import { getInstanceByRef, isSymbol, isSymbolLink } from 'utils/blocks/symbol';
import { getAssetURL } from 'utils/assets';
import { findComponentBy } from 'utils/blocks/find-component-by';
import { componentWalkFull } from 'utils/blocks/component-walk-full';
import { hasCardFeature } from 'src/common/utils/facades/utils';
import { componentWalk } from 'src/common/utils/blocks/component-walk';
import * as T from './types';

type Keywords = null | T.KeywordsResponse['response']['keywords'];

type BaseData = null | T.StoryBaseResponse['response'][number];

type Characters = T.CharactersResponse['response'];

type CharacterIdMap = Map<CardFlowEvent['name'], CardFlowEvent['_id']>;

type CardsData = { [cardId: string]: T.CardResponse['response']['output'] };

type StoryData = {
	name: StoryModel['name'];
	type: Exclude<StoryModel['type'], null>;
	language: StoryModel['language'];
	tags: StoryModel['tags'];
	storyData: StoryVersionType['data'];
	storySettings: StorySettingsType;
	slug: string;
};

class DataUpdater {
	static updateStoryName(draftStory: StoryData, name: string) {
		draftStory.name = name;
	}

	static updateStorySlug(draftStory: StoryData, slug: string) {
		draftStory.slug = slug;
	}

	static updateSEO(draftSettings: StorySettingsType, keywords: Keywords, base: BaseData) {
		draftSettings.seo = {
			...draftSettings.seo,
			...(keywords ? { keywords: keywords.join(',') } : null),
			...(base ? { title: base.title } : null),
			...(base ? { description: base.description } : null),
		};
	}

	static updateShare(draftSettings: StorySettingsType, base: BaseData) {
		draftSettings.share = {
			...draftSettings.share,
			...(base ? { title: base.title } : null),
			...(base ? { description: base.description } : null),
		};
	}

	static updateCardName(draftCard: CardData, name: string) {
		draftCard.name = name;
	}

	static updateAnswerIsCorrect(
		draftSettings: StorySettingsType,
		params: { cardId: string; answerId: string; isCorrect: boolean }
	) {
		set(draftSettings, ['cards', params.cardId, 'answers', params.answerId, 'isCorrect'], params.isCorrect);
	}

	static updateAnswerScore(
		draftSettings: StorySettingsType,
		params: { cardId: string; answerId: string; score: number }
	) {
		set(draftSettings, ['cards', params.cardId, 'answers', params.answerId, 'score'], params.score);
	}

	// static updateAnswerCharacterPoints(
	// 	draftSettings: StorySettingsType,
	// 	params: {
	// 		cardId: string;
	// 		answerId: string;
	// 		points: T.PersonalityCardResponse['response']['answers'][number]['scores'];
	// 		charactersMap: CharacterIdMap;
	// 	}
	// ) {
	// 	set(
	// 		draftSettings,
	// 		['cards', params.cardId, 'answers', params.answerId, characterPoints],
	// 		Object.fromEntries(
	// 			[...params.charactersMap.entries()].map(([characterName, characterId]) => [
	// 				characterId,
	// 				params.points[characterName] ?? 0,
	// 			])
	// 		)
	// 	);
	// }

	static updateNavigationCharacterEvents(
		draftCards: CardData[],
		params: { storySettings: StorySettingsType; characters: FormattedContent['characters'] }
	): CharacterIdMap {
		const charactersMap: CharacterIdMap = new Map();

		draftCards.forEach(draftCard => {
			const cardSettings = params.storySettings.cards?.[draftCard._id] ?? {};
			const isCharacterCountType = cardSettings.countType === CountType.character;

			if (isCharacterCountType) {
				draftCard.events.forEach(draftEvent => {
					const character = params.characters.find(c => c.id === draftEvent._id);

					if (character) {
						charactersMap.set(character.name, draftEvent._id);
						draftEvent.name = character.name;
					}
				});
			}
		});

		return charactersMap;
	}

	static updateTextChildNode(
		draftComponent: BBModel,
		text: string | undefined,
		params: { platform: StoryMediaPlatform }
	) {
		if (text) {
			draftComponent.children = {
				[COMPONENT_STATES.DEFAULT]: { [params.platform]: text },
			} as BBModelTextChild;
		}
	}

	static updateImageSource(
		draftComponent: BBModel,
		image: T.GeneratedImageValue | undefined,
		params: { platform: StoryMediaPlatform; state: BBStates }
	) {
		if (image) {
			set(
				draftComponent.uiConfig.componentProps,
				['styles', params.state, params.platform, 'backgroundImage'],
				getAssetURL({ filepath: image.key, hosting: image.hosting })
			);

			// Size of the image and component block is unknown, without image parsing it is not possible to assign
			// correct backgroundPosition which is further assigned to "rect" query aimed to render designed image area.
			// Temporarily it is decided to hardcode "0,0,1024,1024" which is in most fits better than
			// backgroundPosition from configured in the original template
			// todo: Recommended solution is to parse image as you do when loading image from editor to get valid props
			set(
				draftComponent.uiConfig.componentProps,
				['styles', params.state, params.platform, 'backgroundPosition'],
				'0,0,1024,1024'
			);
		}
	}
}

interface FormattedContent {
	keywords: Keywords;
	base: BaseData;
	characters: Characters;
	cards: CardsData;
}

const getFormattedContent = (content: T.PromptsResponseList): FormattedContent => {
	return [...content]
		.sort((a, b) => a.id - b.id)
		.reduce(
			(acc, item) => {
				switch (item.type) {
					case 'GetKeywords':
						acc.keywords = item.response.keywords;
						break;
					case 'GetStoryBaseData':
						acc.base = item.response[0] ?? null;
						break;
					case 'GetCard': {
						const { cardId, output } = item.response;
						acc.cards[cardId] = output;
						break;
					}
					default:
						break;
				}
				return acc;
			},
			{ keywords: null, base: null, characters: [], cards: {} } as FormattedContent
		);
};

function getCardTitle(card: FormattedContent['cards'][string]): string {
	if (typeof card['card.name'] === 'string') {
		return card['card.name'];
	}

	return 'No title';
}

const GENERATOR_DEFAULT_CONFIG = {
	storyName: true,
	storySlug: true,
	seo: true,
	share: true,
	navigationCharacters: true,
	/**
	 * Flag to determine which cards data should be updated
	 *
	 * true     - update all cards
	 * false    - no cards update
	 * string[] - update card by id from the given array
	 */
	cards: true as boolean | string[],
};

// todo: [ ] missing keywords
// todo: [ ] missing characters to update character events in navigation cards

/**
 * Injects generated content into the story. Any modifications
 * made by this function should be performed through the `DataUpdater`
 * class to ensure proper data management and consistency.
 */
export function injectGeneratedContent(params: {
	story: StoryData;
	content: T.PromptsResponseList;
	config?: Partial<typeof GENERATOR_DEFAULT_CONFIG>;
	platform: StoryMediaPlatform;
	state: BBStates;
}) {
	const { state, platform } = params;
	const config = { ...GENERATOR_DEFAULT_CONFIG, ...params.config };
	const content = getFormattedContent(params.content);
	const story = deepClone(params.story);

	// =============================== Update story ===============================

	// const result = produce(params.story, story => {
	if (config.storyName && content.base?.title) DataUpdater.updateStoryName(story, content.base.title);
	if (config.storySlug && content.base?.slug) DataUpdater.updateStorySlug(story, content.base.slug);
	if (config.seo) DataUpdater.updateSEO(story.storySettings, content.keywords, content.base);
	if (config.share) DataUpdater.updateShare(story.storySettings, content.base);

	// count cards by type
	const cardCounter = new Counter<CardData['type']>({ startFrom: -1 });

	const cards = story.storyData.steps.reduce((acc, step) => acc.concat(step.cards), [] as CardData[]);
	const navCards = cards.filter(card => card.type === CARD_TYPE.NAVIGATION);

	// fixme: content.characters is empty array for now
	const charactersMap = DataUpdater.updateNavigationCharacterEvents(config.navigationCharacters ? navCards : [], {
		storySettings: story.storySettings,
		characters: content.characters,
	});

	const symbols = story.storyData.symbols ?? {};

	cards.forEach(card => {
		cardCounter.increment(card.type);
		const cardType = card.type;
		const cardContent = content.cards[card._id];
		const cardSettings = story.storySettings.cards?.[card._id] ?? {};
		const isCharacterCountType = cardSettings.countType === CountType.character;

		if (
			!cardContent ||
			!config.cards ||
			(Array.isArray(config.cards) && !config.cards.find(id => card._id === id))
		) {
			return;
		}

		const cardTitle = getCardTitle(cardContent);

		DataUpdater.updateCardName(card, cardTitle);

		const groups: Array<{
			key: string;
			component: BBModel;
			path: string;
			index: number;
			parent: BBModel | undefined;
		}> = [];

		const deletions = new Set<string>();
		const idsMap = createIdsMap();
		const collectIds: GenerateIdCallback = ({ oldId, ...data }) => idsMap.set(oldId, data);
		const duplicatesMap = new Map<
			string,
			Array<{
				component: BBModel | BBSymbolLink;
				context: Record<string, T.GeneratedValue>;
			}>
		>();
		const newSymbolInstanceArr: BBSymbolInstance[] = [];

		function duplicateInstance(baseInstance: BBSymbolInstance): BBSymbolInstance {
			const { createInstanceFromInstance } = new SymbolManager({ errorClass: AdminError });
			let newInstance = createInstanceFromInstance(baseInstance, collectIds);
			[newInstance] = updateReferenceIds([newInstance], idsMap, symbols);
			newSymbolInstanceArr.push(newInstance);
			return newInstance;
		}

		function findSiblingGroup(parent: BBModel | undefined) {
			return groups.find(g => g.parent?._id === parent?._id);
		}

		// 1. Mark duplicates and deletions
		componentWalkFull({
			elements: card.elements,
			storyElements: story.storyData.elements,
			symbols,
			callback: function walk({ component, path, index, parent }) {
				const { aiTemplate } = component.uiConfig.componentProps;

				if (aiTemplate?.type === 'group') {
					const siblingGroup = findSiblingGroup(parent);
					if (!siblingGroup || siblingGroup.key !== aiTemplate.key) {
						groups.push({ key: aiTemplate.key, component, path, index, parent });
					}
				}

				const siblingGroup = findSiblingGroup(parent);

				if (
					siblingGroup &&
					siblingGroup.index < index && // only later siblings
					siblingGroup.component.type === component.type && // same type
					(!aiTemplate || siblingGroup.key === aiTemplate.key)
				) {
					deletions.add(component.symbol?.instanceId ?? component._id);
				}
			},
		});

		// 2. Create duplicates
		groups.forEach(group => {
			const originalComponent = group.component;
			const groupContent = cardContent[group.key];
			const duplicatesCount = Array.isArray(groupContent) ? groupContent.length : 0;

			duplicatesMap.set(group.key, []);

			for (let i = 0; i < duplicatesCount; i += 1) {
				const clone = deepClone(originalComponent);
				const context = Array.isArray(groupContent) ? groupContent[i] : {};
				let finalComponent: BBModel | BBSymbolLink;

				// todo: suggest refactor, this part is duplicate from `pasteComponent`
				if (isSymbol(clone)) {
					const newInstance = duplicateInstance(clone);
					finalComponent = { symbol: { ...newInstance.symbol } };
				} else {
					componentWalk([clone], function walk({ component: draftComponent }) {
						if (isSymbolLink(draftComponent)) {
							const originalInstance = getInstanceByRef(draftComponent.symbol, symbols);
							if (!originalInstance) throw new AdminError('Instance not found.');
							const newInstance = duplicateInstance(originalInstance);
							draftComponent.symbol.instanceId = newInstance._id;
							return;
						}

						const oldId = draftComponent._id;
						updateId(draftComponent);
						collectIds({ oldId, newId: draftComponent._id, type: draftComponent.type });
						componentWalk(draftComponent.children, walk);
					});
					// Add final clone to duplicates map
					const [finalClone] = updateReferenceIds([clone], idsMap, symbols);
					finalComponent = finalClone;
				}

				// Store duplicate with its context
				duplicatesMap.get(group.key)!.push({
					component: finalComponent,
					context,
				});
			}
		});

		// 3.1 Append duplicates
		groups.forEach(group => {
			const { parent } = group;
			if (!parent) return;

			const duplicatesToAppend = duplicatesMap.get(group.key)?.map(item => item.component);
			if (!Array.isArray(parent.children) || !duplicatesToAppend) return;
			parent.children.splice(group.index, 1, ...(duplicatesToAppend as BBModel[]));
		});

		// 3.1.1 Append symbol new instances
		newSymbolInstanceArr.forEach(instance => {
			if (!symbols[instance.symbol.masterId]) return;
			symbols[instance.symbol.masterId].instance[instance._id] = instance;
		});

		// 3.2 Remove deletions
		groups.forEach(group => {
			const { parent } = group;
			if (!parent) return;

			if (!Array.isArray(parent.children)) return;
			parent.children = parent.children.filter(
				child => !deletions.has(child._id) && !deletions.has(child.symbol?.instanceId ?? '')
			);
		});

		const componentToContextMap = new Map<string, { index: number; context: Record<string, T.GeneratedValue> }>();

		duplicatesMap.forEach(duplicates => {
			duplicates.forEach(({ component, context }, index) => {
				const componentId = isSymbolLink(component) ? component.symbol.instanceId : component._id;
				componentToContextMap.set(componentId, { index, context });
			});
		});

		let currentGroupContext: {
			path: string;
			index: number;
			context: Record<string, T.GeneratedValue>;
		} | null = null;

		componentWalkFull({
			elements: card.elements,
			storyElements: story.storyData.elements,
			symbols,
			callback: function walk({ component, path }) {
				const aiTemplate = component?.uiConfig?.componentProps?.aiTemplate;

				if (!aiTemplate) {
					return;
				}

				let targetComponent: BBModel | undefined = component;

				if (isSymbol(component)) {
					const { masterId, instanceId, childId } = component.symbol;
					const symbol = story.storyData.symbols![masterId];
					const instance = symbol.instance[instanceId] as BBSymbolInstance;

					targetComponent = childId
						? findComponentBy([instance], { path: 'symbol.childId', value: childId })?.component
						: instance;
				}

				// This case should not happen, but added for a save
				if (!targetComponent) {
					return;
				}

				// Reset current group context if we moved out of it
				if (currentGroupContext && !path.startsWith(currentGroupContext.path)) {
					currentGroupContext = null;
				}

				// Define context
				const context =
					componentToContextMap.get(targetComponent._id)?.context ||
					currentGroupContext?.context ||
					cardContent;

				// If this is a direct duplicate, update the current group context
				if (componentToContextMap.has(targetComponent._id)) {
					currentGroupContext = {
						path,
						...componentToContextMap.get(targetComponent._id)!,
					};
				}

				switch (aiTemplate.type) {
					case 'image': {
						const value = context[aiTemplate.key] as T.GeneratedImageValue;
						DataUpdater.updateImageSource(targetComponent, value, { platform, state });
						break;
					}
					case 'text': {
						const value = context[aiTemplate.key] as T.GeneratedTextValue;
						DataUpdater.updateTextChildNode(targetComponent, value, { platform });
						break;
					}
					case 'group': {
						if (component.type === COMPONENT_TYPE.ANSWER) {
							if (hasCardFeature(cardType).correct) {
								const isCorrect =
									'isCorrect' in context && typeof context.isCorrect === 'boolean'
										? context.isCorrect
										: null;

								if (isCorrect) {
									DataUpdater.updateAnswerIsCorrect(story.storySettings, {
										cardId: card._id,
										answerId: component._id,
										isCorrect,
									});
								}
							}

							if (hasCardFeature(cardType).answerScore) {
								// add answer score for Personality Test cards
								const score =
									'score' in context && typeof context.score === 'number' ? context.score : null;

								if (score !== null) {
									DataUpdater.updateAnswerScore(story.storySettings, {
										cardId: card._id,
										answerId: component._id,
										score,
									});
								}
							}

							if (isCharacterCountType) {
								console.info('❌ Character points not implemented yet.', charactersMap);
								// DataUpdater.updateAnswerCharacterPoints(story.storySettings, {
								// 	cardId: card._id,
								// 	answerId: component._id,
								// 	points: answer.scores,
								// 	charactersMap,
								// });
							}
						}

						break;
					}
					default:
						break;
				}

				// Remove aiTemplate metadata from duplicated components (keeping only on the original template)
				if (currentGroupContext?.context && currentGroupContext.index > 0) {
					// delete targetComponent.uiConfig.componentProps.aiTemplate;
					if (isSymbol(targetComponent)) {
						targetComponent.uiConfig.componentProps.aiTemplate = null;
					} else {
						delete targetComponent.uiConfig.componentProps.aiTemplate;
					}
				}
			},
		});
	});

	return story;
}
